import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Eye, EyeOff } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-notes-preview/artifacts/qfix72o2_Lumero_Logo_transparent_with_name-removebg-preview.png";

export default function SignupPage() {
  const [form, setForm] = useState({ full_name: "", email: "", password: "", clinic_name: "" });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await signup(form.email, form.password, form.full_name, form.clinic_name);
      navigate("/app/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4" data-testid="signup-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Link to="/"><img src={LOGO_URL} alt="LumeroFlow" className="h-9 mx-auto mb-6" /></Link>
          <h1 className="text-2xl font-medium text-[#1A1B1A] mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Create your clinic</h1>
          <p className="text-sm text-[#757675]">Set up your LumeroFlow workspace</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl" data-testid="signup-error">{error}</div>}
          <div>
            <label className="block text-sm font-medium text-[#1A1B1A] mb-1.5">Your name</label>
            <input type="text" value={form.full_name} onChange={update("full_name")} required data-testid="signup-name-input"
              className="w-full px-4 py-3 rounded-xl border border-[#1A1B1A]/10 bg-white text-sm text-[#1A1B1A] placeholder-[#757675] focus:outline-none focus:border-[#205B5A] focus:ring-1 focus:ring-[#205B5A] transition-colors"
              placeholder="Dr. Jane Smith" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1A1B1A] mb-1.5">Clinic name</label>
            <input type="text" value={form.clinic_name} onChange={update("clinic_name")} required data-testid="signup-clinic-input"
              className="w-full px-4 py-3 rounded-xl border border-[#1A1B1A]/10 bg-white text-sm text-[#1A1B1A] placeholder-[#757675] focus:outline-none focus:border-[#205B5A] focus:ring-1 focus:ring-[#205B5A] transition-colors"
              placeholder="Your Clinic Name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1A1B1A] mb-1.5">Email</label>
            <input type="email" value={form.email} onChange={update("email")} required data-testid="signup-email-input"
              className="w-full px-4 py-3 rounded-xl border border-[#1A1B1A]/10 bg-white text-sm text-[#1A1B1A] placeholder-[#757675] focus:outline-none focus:border-[#205B5A] focus:ring-1 focus:ring-[#205B5A] transition-colors"
              placeholder="you@clinic.com" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1A1B1A] mb-1.5">Password</label>
            <div className="relative">
              <input type={showPw ? "text" : "password"} value={form.password} onChange={update("password")} required data-testid="signup-password-input"
                className="w-full px-4 py-3 rounded-xl border border-[#1A1B1A]/10 bg-white text-sm text-[#1A1B1A] placeholder-[#757675] focus:outline-none focus:border-[#205B5A] focus:ring-1 focus:ring-[#205B5A] transition-colors pr-10"
                placeholder="Min. 6 characters" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#757675]">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <button type="submit" disabled={loading} data-testid="signup-submit-btn"
            className="w-full bg-[#205B5A] text-white py-3 rounded-full font-medium text-sm hover:bg-[#174544] transition-colors disabled:opacity-50">
            {loading ? "Creating workspace..." : "Create workspace"}
          </button>
        </form>
        <p className="text-center text-sm text-[#757675] mt-6">
          Already have an account? <Link to="/app/login" className="text-[#205B5A] font-medium hover:text-[#174544]" data-testid="goto-login-link">Sign in</Link>
        </p>
        <p className="text-center mt-4"><Link to="/" className="text-xs text-[#757675] hover:text-[#4A4B4A]">Back to website</Link></p>
      </div>
    </div>
  );
}
